import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Minus, Package, ScanBarcode, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BarcodeScanDialog from "@/components/common/BarcodeScanDialog";
import { cn } from "@/lib/utils";
import { createPageUrl } from "@/utils";
import {
  matchesFlexibleProductSearch,
  sortProductsByFlexibleSearchRelevance,
} from "@/utils/flexibleProductSearch";
import { clampCartQuantity, isProductOrderable } from "@/utils/productAvailability";
import { swallowClick, handleQtyTapAllowingScroll } from "@/utils/scrollSafeTap";
import { useLocalStore } from "@/contexts/LocalStoreContext";
import { toast } from "sonner";
import { normalizeProductId, cartQtyForProductId } from "@/utils/productIds";
import {
  getOfflineCart,
  normalizeStoreId,
} from "@/lib/offlineCart";
import { pushCartItemChange } from "@/lib/cartSync";
import { useCartItemsQuery } from "@/hooks/useCartItemsQuery";
import PrefetchImage from "@/components/common/PrefetchImage";
import { getProxyImageUrl } from "@/lib/prefetchStorage";
import { usePromotedCompanies } from "@/contexts/PromotedCompaniesContext";
import { useAuth } from "@/auth/AuthContext";
import { useStoreSession } from "@/hooks/useStoreSession";
import { base44 } from "@/api/base44Client";
import {
  buildPromotionsByProductId,
  getPromotionDisplayBadgesFromList,
  getPromotionDealLines,
  isPromotionVisible,
} from "@/utils/promotionStatus";
import {
  getDistributionBadgeState,
  getDistributionBadgeStateWithFreshMap,
} from "@/utils/distributionBadge";
import { shouldPreferLocalData } from "@/lib/connectionQuality";

const MAX_RESULTS = 50;
let collapseGuardUntil = 0;

function cartQtyForProduct(cartItems, product) {
  return cartQtyForProductId(cartItems, product);
}

/**
 * חיפוש מוצרים מהיר בראש המסך — דרופדאון עם +/- לסל (מגוון מ־prefetch).
 */
export default function HeaderQuickProductSearch({ products = [], storeId: storeIdProp, canEditCart, className, expanded = false, onExpandedChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { branchId } = useStoreSession();
  const storeId = normalizeStoreId(storeIdProp);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [dropdownTop, setDropdownTop] = useState(0);
  const [dropdownMaxH, setDropdownMaxH] = useState(320);
  const rootRef = useRef(null);
  const inputWrapRef = useRef(null);
  const lastCartInteractAt = useRef(0);
  const ignoreCollapseUntilRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 220);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onDoc = (e) => {
      if (Date.now() < ignoreCollapseUntilRef.current || Date.now() < collapseGuardUntil) return;
      if (rootRef.current?.contains(e.target)) return;
      setOpen(false);
      if (expanded && !query.trim()) onExpandedChange?.(false);
    };
    document.addEventListener("pointerdown", onDoc);
    return () => document.removeEventListener("pointerdown", onDoc);
  }, [expanded, query, onExpandedChange]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const main = document.querySelector("main");
    const onScroll = () => {
      const active = document.activeElement;
      if (rootRef.current?.contains(active)) return;
      setOpen(false);
    };
    main?.addEventListener("scroll", onScroll, { passive: true });
    return () => main?.removeEventListener("scroll", onScroll);
  }, [open]);

  const updateDropdownPosition = useCallback(() => {
    const el = inputWrapRef.current || rootRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().bottom + 4;
    const viewportH = window.visualViewport?.height ?? window.innerHeight;
    setDropdownTop(top);
    setDropdownMaxH(Math.max(160, Math.round(viewportH - top - 12)));
  }, []);

  useLayoutEffect(() => {
    if (!debounced || !open) return;
    updateDropdownPosition();
    window.addEventListener("resize", updateDropdownPosition);
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.visualViewport?.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("resize", updateDropdownPosition);
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.visualViewport?.removeEventListener("resize", updateDropdownPosition);
    };
  }, [debounced, open, updateDropdownPosition]);

  const { data: cartItems = [] } = useCartItemsQuery(storeId, { enabled: !!storeId });
  const { promotedCompanyIds } = usePromotedCompanies();
  const { localData } = useLocalStore();
  const centralAccountId = localData?.store?.central_account_id ?? null;

  const { data: promotionsData = [] } = useQuery({
    queryKey: ["promotions"],
    queryFn: () => base44.entities.Promotion.filter({ is_active: true }),
    enabled: !!user,
  });
  const promotions = Array.isArray(promotionsData) ? promotionsData : (promotionsData?.data || []);
  const storeChainId = localData?.store?.chain_id?._id ?? localData?.store?.chain_id ?? null;
  const promotionsByProductId = useMemo(
    () => buildPromotionsByProductId(promotions, storeId, storeChainId),
    [promotions, storeId, storeChainId]
  );

  const results = useMemo(() => {
    if (!debounced || !products?.length) return [];
    const list = sortProductsByFlexibleSearchRelevance(
      products.filter((p) => matchesFlexibleProductSearch(p, debounced)),
      debounced,
      promotedCompanyIds
    );
    // זמינים להזמנה קודם (לפי רלוונטיות), לא-זמינים בתחתית
    const orderable = [];
    const notOrderable = [];
    for (const p of list) {
      if (isProductOrderable(p, { centralAccountId })) orderable.push(p);
      else notOrderable.push(p);
    }
    return [...orderable, ...notOrderable].slice(0, MAX_RESULTS);
  }, [products, debounced, promotedCompanyIds, centralAccountId]);

  const distIdsNeedingFresh = useMemo(() => {
    if (!storeId || !open || !debounced || !results.length || shouldPreferLocalData()) {
      return [];
    }
    const ids = [];
    for (const p of results) {
      const state = getDistributionBadgeState(p, storeId);
      if (!state.needsFresh) continue;
      ids.push(normalizeProductId(p.id ?? p._id));
      if (ids.length >= 8) break;
    }
    return ids;
  }, [results, storeId, open, debounced]);

  const { data: freshDistByProductId = {} } = useQuery({
    queryKey: ["headerSearchDistribution", storeId, distIdsNeedingFresh.join(",")],
    queryFn: async () => {
      const out = {};
      await Promise.all(
        distIdsNeedingFresh.map(async (id) => {
          try {
            const prod = await base44.entities.Product.get(id);
            out[id] = prod?.distribution_by_store ?? prod?.distributionByStore ?? null;
          } catch {
            out[id] = null;
          }
        })
      );
      return out;
    },
    enabled: distIdsNeedingFresh.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const applyCartQty = useCallback(
    (product, quantity) => {
      if (!canEditCart || !storeId || !product) return;
      const id = normalizeProductId(product.id ?? product._id);
      const supplier_id = product.supplier_id || product.supplierId;
      pushCartItemChange(storeId, {
        product_id: id,
        store_id: storeId,
        branch_id: branchId || "",
        quantity,
        supplier_id: supplier_id?._id || supplier_id,
        supplier_name: supplier_id?.name ?? product.supplier?.name ?? null,
        product: {
          _id: id,
          name: product.name,
          barcode: product.barcode,
          imageUrl: product.image_url || product.imageUrl,
          image_url: product.image_url || product.imageUrl,
          units_in_carton: product.units_in_carton ?? product.unitsInCarton,
          unitsInCarton: product.unitsInCarton ?? product.units_in_carton,
          price: product.price,
        },
      }, branchId);
      queryClient.setQueryData(
        ["cartItems", storeId, branchId || "main"],
        getOfflineCart(storeId, branchId)
      );
    },
    [canEditCart, storeId, branchId, queryClient]
  );

  const setQty = useCallback(
    (product, next) => {
      const cart = getOfflineCart(storeId, branchId) || [];
      const id = normalizeProductId(product.id ?? product._id);
      const existing = cart.find((i) => String(i.product_id?._id || i.product_id) === String(id));
      const prevQty = Number(existing?.quantity ?? 0);
      const orderable = isProductOrderable(product, { centralAccountId });
      if (!orderable && next > prevQty) {
        toast.warning("לא זמין להזמנה");
        return;
      }
      const { quantity: q, capped } = clampCartQuantity(product, next, prevQty);
      if (capped && q > 0) {
        toast.warning(`מקסימום ${q.toLocaleString("he-IL")} מארזים (לפי מלאי במחסן)`);
      } else if (capped && q === 0 && next > 0) {
        toast.warning("אין מלאי במחסן");
        return;
      }
      applyCartQty(product, q);
    },
    [applyCartQty, storeId, branchId, centralAccountId]
  );

  const dismissSearchKeyboard = useCallback(() => {
    inputRef.current?.blur();
    if (document.activeElement instanceof HTMLElement && rootRef.current?.contains(document.activeElement)) {
      document.activeElement.blur();
    }
  }, []);

  const bumpQty = useCallback(
    (product, delta) => {
      lastCartInteractAt.current = Date.now();
      dismissSearchKeyboard();
      const current = cartQtyForProduct(getOfflineCart(storeId, branchId) || [], product);
      setQty(product, current + delta);
    },
    [storeId, branchId, setQty, dismissSearchKeyboard]
  );

  const onQtyPointerDown = useCallback((e, product, delta) => {
    handleQtyTapAllowingScroll(e, () => bumpQty(product, delta));
  }, [bumpQty]);

  const showDropdown = open && debounced.length > 0 && results.length > 0;

  const handleBarcodeScan = useCallback((code) => {
    onExpandedChange?.(true);
    setQuery(code);
    setDebounced(String(code || "").trim());
    setOpen(true);
  }, [onExpandedChange]);

  useEffect(() => {
    let buf = "";
    let lastAt = 0;

    const isEditableTarget = (el) => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
      return el.isContentEditable;
    };

    const flushBarcode = (code) => {
      const value = String(code || "").trim();
      if (value.length < 6) return false;
      onExpandedChange?.(true);
      setQuery(value);
      setDebounced(value);
      setOpen(true);
      return true;
    };

    const onKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const now = Date.now();
      const gap = now - lastAt;
      lastAt = now;
      const target = e.target;
      const inOurSearch = rootRef.current?.contains(target);

      if (e.key === "Enter") {
        if (buf.length >= 6 && gap < 120) {
          e.preventDefault();
          flushBarcode(buf);
        } else if (inOurSearch) {
          e.preventDefault();
          const value = (query || buf).trim();
          if (value) {
            setDebounced(value);
            setOpen(true);
          }
        }
        buf = "";
        return;
      }

      if (e.key.length !== 1 || !/[0-9A-Za-z]/.test(e.key)) {
        buf = "";
        return;
      }

      if (!inOurSearch && isEditableTarget(target)) {
        buf = "";
        return;
      }

      if (gap > 120) buf = "";
      buf += e.key;
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [query, onExpandedChange]);

  const inputRef = useRef(null);

  useEffect(() => {
    if (!expanded) {
      setOpen(false);
      return undefined;
    }
    const t = window.setTimeout(() => inputRef.current?.focus(), 20);
    return () => window.clearTimeout(t);
  }, [expanded]);

  const expandSearch = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    ignoreCollapseUntilRef.current = Date.now() + 800;
    collapseGuardUntil = Date.now() + 800;
    onExpandedChange?.(true);
  }, [onExpandedChange]);

  const collapseSearch = useCallback(() => {
    setQuery("");
    setDebounced("");
    setOpen(false);
    onExpandedChange?.(false);
  }, [onExpandedChange]);

  return (
    <div ref={rootRef} className={cn("relative min-w-0", expanded ? "w-full" : "shrink-0", className)}>
      {!expanded ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="relative min-h-11 min-w-11"
          aria-label="חיפוש מוצר"
          onPointerDown={expandSearch}
          onClick={expandSearch}
        >
          <Search className="w-5 h-5 text-gray-600" />
        </Button>
      ) : (
      <div ref={inputWrapRef} className="relative flex items-center gap-1 min-w-0 w-full">
        <div className="relative flex-1 min-w-0">
        <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <button
          type="button"
          className="absolute left-1.5 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-md text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
          aria-label="סריקת ברקוד"
          onClick={() => flushSync(() => setIsCameraOpen(true))}
        >
          <ScanBarcode className="w-5 h-5" />
        </button>
        <Input
          ref={inputRef}
          type="search"
          enterKeyHint="search"
          placeholder="חיפוש מוצר..."
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              collapseSearch();
              return;
            }
            if (e.key !== "Enter") return;
            e.preventDefault();
            setDebounced(query.trim());
            setOpen(true);
          }}
          className="h-11 pr-9 pl-11 text-sm bg-gray-50 border-gray-200"
          autoComplete="off"
          aria-expanded={showDropdown}
          aria-controls="header-quick-search-results"
        />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 min-h-11 min-w-11"
          aria-label="סגור חיפוש"
          onClick={collapseSearch}
        >
          <X className="w-5 h-5 text-gray-600" />
        </Button>
      </div>
      )}

      <BarcodeScanDialog
        open={isCameraOpen}
        onOpenChange={setIsCameraOpen}
        onScan={handleBarcodeScan}
        readerId="header-quick-barcode-reader"
        manualValue={query}
        onManualChange={(value) => {
          setQuery(value);
          setOpen(true);
        }}
      />

      {debounced && open && (
        <div
          id="header-quick-search-results"
          role="listbox"
          style={{ top: dropdownTop, maxHeight: dropdownMaxH }}
          className={cn(
            "fixed inset-x-0 z-[200] border-b border-gray-200 bg-white shadow-[0_12px_24px_-8px_rgba(0,0,0,0.15)] overflow-hidden",
            results.length === 0 ? "py-3 px-4 text-center text-sm text-gray-500" : "overflow-y-auto overscroll-contain px-2 sm:px-4"
          )}
        >
          {results.length === 0 ? (
            <span>לא נמצאו מוצרים</span>
          ) : (
            <ul className="divide-y divide-gray-100">
              {results.map((p) => {
                const id = normalizeProductId(p.id ?? p._id);
                const qty = cartQtyForProduct(cartItems, p);
                const img = p.image_url || p.imageUrl;
                const imgSrc = getProxyImageUrl(img, true) || img;
                const productPromotions = promotionsByProductId.get(id) || [];
                const activePromotions = productPromotions.filter(
                  (pr) => isPromotionVisible(pr)
                );
                const promotionBadges =
                  activePromotions.length > 0
                    ? getPromotionDisplayBadgesFromList(activePromotions)
                    : [];
                const dealLines = getPromotionDealLines(activePromotions);
                const orderable = isProductOrderable(p, { centralAccountId });
                const showCartControls = orderable || qty > 0;
                const distState = getDistributionBadgeStateWithFreshMap(
                  p,
                  storeId,
                  freshDistByProductId[id]
                );
                return (
                  <li
                    key={id}
                    className="flex items-center gap-2 p-2 min-h-[52px] hover:bg-emerald-50/60 transition-colors"
                  >
                    <button
                      type="button"
                      className="flex flex-1 min-w-0 items-center gap-2 text-right"
                      onClick={() => {
                        if (Date.now() - lastCartInteractAt.current < 600) return;
                        navigate(createPageUrl(`ProductDetails?id=${id}`));
                        setOpen(false);
                        setQuery("");
                      }}
                    >
                      <div className="w-10 h-10 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                        {imgSrc ? (
                          <PrefetchImage src={imgSrc} alt="" className="w-full h-full object-contain p-1" />
                        ) : (
                          <Package className="w-5 h-5 text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900 line-clamp-2 leading-tight">{p.name}</p>
                        {(promotionBadges.length > 0 || distState.show) ? (
                          <div className="flex flex-wrap gap-0.5 mt-0.5">
                            {distState.show ? (
                              <Badge className="bg-sky-600/95 text-white text-[11px] px-1.5 py-0.5 h-5 font-bold shadow-sm border-0">
                                {distState.label}
                              </Badge>
                            ) : null}
                            {promotionBadges.map((badge) => (
                              <Badge
                                key={badge.key}
                                variant={badge.variant || "default"}
                                className={`text-[9px] px-1 py-0 h-4 font-semibold shadow-sm ${badge.className}`}
                              >
                                {badge.label}
                              </Badge>
                            ))}
                          </div>
                        ) : null}
                        {dealLines.length > 0 ? (
                          <div className="mt-0.5 space-y-0.5">
                            {dealLines.slice(0, 2).map((line) => (
                              <p
                                key={line.key}
                                className={`text-[10px] font-semibold leading-tight line-clamp-1 ${
                                  line.timeStatus === "future" ? "text-sky-700" : "text-emerald-700"
                                }`}
                              >
                                {line.timeStatus === "future" ? "עתידי · " : ""}
                                {line.description || "מבצע"}
                                {line.dates ? ` · ${line.dates}` : ""}
                              </p>
                            ))}
                          </div>
                        ) : null}
                        {p.barcode ? (
                          <p className="text-[11px] text-gray-500 truncate">{p.barcode}</p>
                        ) : null}
                      </div>
                    </button>
                    <div
                      className="flex items-center gap-0.5 shrink-0 touch-manipulation"
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      {showCartControls ? (
                        <>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg border-emerald-200 text-emerald-700 touch-manipulation"
                            disabled={!canEditCart || qty <= 0}
                            aria-label="הפחת כמות"
                            onPointerDown={(e) => onQtyPointerDown(e, p, -1)}
                            onClick={swallowClick}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="w-7 text-center text-sm font-semibold tabular-nums">{qty}</span>
                          <Button
                            type="button"
                            size="icon"
                            className="h-11 w-11 min-h-[44px] min-w-[44px] rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300 touch-manipulation"
                            disabled={!canEditCart || !orderable}
                            aria-label="הוסף לסל"
                            onPointerDown={(e) => onQtyPointerDown(e, p, 1)}
                            onClick={swallowClick}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <span className="text-[10px] text-gray-500 font-medium px-1 whitespace-nowrap">
                          לא זמין להזמנה
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
